import { createEffect, onCleanup, onMount, createSignal, type ParentProps } from "solid-js";
import type { ViewStore } from "../stores/view";

export type ResolvedTheme = "fastdiff-light" | "fastdiff-dark";

interface ThemeProviderProps {
  view: ViewStore;
}

function prefersDark(): boolean {
  return typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider(p: ParentProps<ThemeProviderProps>) {
  const [systemDark, setSystemDark] = createSignal<boolean>(prefersDark());

  onMount(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", handler);
      onCleanup(() => mq.removeEventListener("change", handler));
    } else if (typeof (mq as unknown as { addListener: (cb: (e: MediaQueryListEvent) => void) => void }).addListener === "function") {
      (mq as unknown as { addListener: (cb: (e: MediaQueryListEvent) => void) => void }).addListener(handler);
      onCleanup(() => (mq as unknown as { removeListener: (cb: (e: MediaQueryListEvent) => void) => void }).removeListener(handler));
    }
  });

  const resolved = (): ResolvedTheme => {
    const t = p.view.theme();
    if (t === "system") return systemDark() ? "fastdiff-dark" : "fastdiff-light";
    return t === "dark" ? "fastdiff-dark" : "fastdiff-light";
  };

  createEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = resolved();
  });

  createEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.wrap = p.view.wrap() ? "on" : "off";
  });

  createEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.fontSize = p.view.fontSize();
  });

  return <>{p.children}</>;
}
